// @flow

import * as React from 'react';
import {useRef, useEffect} from 'react';
import {useAccount, useLocation} from 'docs/data';

import {motion, AnimatePresence} from 'framer-motion';
import Box from 'sail/Box';
import Group from 'sail/Group';
import {Subheading} from 'sail/Text';
import {getExpansionVariants} from 'docs/util/animation';

import type {
  NavigationNode,
  NavigationHeading,
  NavigationLink,
} from 'docs/data';
import {SidebarLink} from './SidebarLink';
import {SidebarInfoTooltip} from './SidebarInfoTooltip';

type SidebarHeadingProps = {|
  node: NavigationHeading,
  indentLevel?: number,
|};

const SidebarHeading = ({node, indentLevel = 0}: SidebarHeadingProps) => (
  <Box
    // $FlowExpectedError - Flow can't do math :(
    padding={{top: 24, left: 12 * indentLevel}}
    className="SidebarSubtree-SidebarHeading"
  >
    <Subheading>{node.text}</Subheading>
  </Box>
);

type SidebarItemProps = {|
  node: NavigationLink,
  indentLevel?: number,
|};

const SidebarItem = ({node, indentLevel = 0}: SidebarItemProps) => {
  const style = node.hidden ? {display: 'none'} : {};
  const ref = useRef();
  const {location} = useLocation();
  const {account} = useAccount();

  const shouldScroll =
    node.selected && !node.hidden && node.href === location.pathname;

  useEffect(() => {
    if (shouldScroll) {
      const sidebarItem = ref.current;
      const sidebarContent = sidebarItem ? sidebarItem.offsetParent : null;

      // Scroll to the new SidebarItem on location changes
      // only if the new SidebItem is outside of the viewport
      if (
        sidebarItem &&
        sidebarContent &&
        (sidebarItem.offsetTop < sidebarContent.scrollTop ||
          sidebarItem.offsetTop >=
            sidebarContent.clientHeight + sidebarContent.scrollTop)
      ) {
        // We have to wait for SidebarNavigation to open / close the right product subtrees
        // before scrolling to the correct link (otherwise we get a jerky experience)
        const timeoutID = setTimeout(() => {
          sidebarItem.scrollIntoView({behavior: 'smooth', block: 'center'});
        }, 500);

        return () => clearTimeout(timeoutID);
      }
    }
  }, [account.locale, account.country, shouldScroll]);

  return (
    <Box
      className="SidebarItem"
      // $FlowExpectedError - Flow can't do math :(
      padding={{top: 8, left: 12 * indentLevel}}
      style={style}
      boxRef={ref}
    >
      <SidebarLink
        href={node.href}
        title={node.description}
        active={node.selected}
        translated={node.translated}
      >
        <span>
          {node.text} <SidebarInfoTooltip xray={node.xray} />
        </span>
      </SidebarLink>
      <AnimatePresence initial={false}>
        {node.expanded && (
          <Box style={{overflow: 'hidden'}}>
            <motion.div
              key={node.href}
              variants={getExpansionVariants()}
              transition={{type: 'spring', damping: 100, stiffness: 1000}}
              initial="collapsed"
              animate="expanded"
              exit="collapsed"
            >
              <SidebarSubtree
                nodes={node.children}
                indentLevel={indentLevel + 1}
              />
            </motion.div>
          </Box>
        )}
      </AnimatePresence>
    </Box>
  );
};

type SidebarSubtreeProps = {|
  nodes: Array<NavigationNode>,
  indentLevel?: number,
  topLevel?: boolean, // Adjust the margin if the subtree is at the top-level (e.g not under a dropdown)
|};

export const SidebarSubtree = ({
  nodes,
  indentLevel = 0,
  topLevel = false,
}: SidebarSubtreeProps) => (
  <Group style={{marginTop: topLevel ? '-8px' : '0px'}}>
    {nodes.map((node, index) => {
      if (node.type === 'heading') {
        return (
          <SidebarHeading key={index} node={node} indentLevel={indentLevel} />
        );
      } else {
        return (
          <SidebarItem key={index} node={node} indentLevel={indentLevel} />
        );
      }
    })}
  </Group>
);
